import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { featureFlagApi } from "../../services/featureFlag.api";
import { FeatureFlagDto } from "../../types/featureFlag.definition";
import {
  createFeatureFlagFailure,
  createFeatureFlagRequest,
  createFeatureFlagSuccess,
  deleteFeatureFlagFailure,
  deleteFeatureFlagRequest,
  deleteFeatureFlagSuccess,
  fetchFeatureFlagsFailure,
  fetchFeatureFlagsRequest,
  fetchFeatureFlagsSuccess,
  updateFeatureFlagFailure,
  updateFeatureFlagRequest,
  updateFeatureFlagSuccess,
} from "../reducers/featureFlagSlice";

// Helper to extract an error code from rejected interceptor errors.
function extractErrorCode(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "code" in error &&
    typeof (error as Record<string, unknown>).code === "string"
  ) {
    return (error as Record<string, unknown>).code as string;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "UNKNOWN_ERROR";
}

function* fetchFeatureFlagsSaga() {
  try {
    const data: FeatureFlagDto[] = yield call(featureFlagApi.getFeatureFlags);
    yield put(fetchFeatureFlagsSuccess(data));
  } catch (error) {
    yield put(fetchFeatureFlagsFailure(extractErrorCode(error)));
  }
}

function* createFeatureFlagSaga(
  action: ReturnType<typeof createFeatureFlagRequest>
) {
  const { data, onSuccess, onError } = action.payload;
  try {
    const rule: FeatureFlagDto = yield call(
      featureFlagApi.createFeatureFlag,
      data
    );
    yield put(createFeatureFlagSuccess(rule));
    if (onSuccess) onSuccess(rule);
  } catch (error) {
    const code = extractErrorCode(error);
    yield put(createFeatureFlagFailure(code));
    if (onError) onError(code);
  }
}

function* updateFeatureFlagSaga(
  action: ReturnType<typeof updateFeatureFlagRequest>
) {
  const { id, data, onSuccess, onError } = action.payload;
  try {
    const rule: FeatureFlagDto = yield call(
      featureFlagApi.updateFeatureFlag,
      id,
      data
    );
    yield put(updateFeatureFlagSuccess(rule));
    if (onSuccess) onSuccess(rule);
  } catch (error) {
    const code = extractErrorCode(error);
    yield put(updateFeatureFlagFailure(code));
    if (onError) onError(code);
  }
}

function* deleteFeatureFlagSaga(
  action: ReturnType<typeof deleteFeatureFlagRequest>
) {
  const { id, onSuccess, onError } = action.payload;
  try {
    yield call(featureFlagApi.deleteFeatureFlag, id);
    yield put(deleteFeatureFlagSuccess(id));
    if (onSuccess) onSuccess();
  } catch (error) {
    const code = extractErrorCode(error);
    yield put(deleteFeatureFlagFailure(code));
    if (onError) onError(code);
  }
}

export function* featureFlagSaga() {
  yield takeLatest(fetchFeatureFlagsRequest, fetchFeatureFlagsSaga);
  yield takeEvery(createFeatureFlagRequest, createFeatureFlagSaga);
  yield takeEvery(updateFeatureFlagRequest, updateFeatureFlagSaga);
  yield takeEvery(deleteFeatureFlagRequest, deleteFeatureFlagSaga);
}
